import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, IconButton, Tooltip, Alert, CircularProgress,
  Checkbox, Collapse, Paper, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Sync as SyncIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getApiError } from '../utils/apiError';
import {
  configSyncService,
  DiffSummaryResponse, TableDiffResponse, RecordDiff,
  ConfigTable, TABLE_LABELS, SyncResultItem, SyncDirection,
  InitializeMappingResponse,
} from '../services/configSyncService';

// Shows the "Initialize Mapping" button (backfills the dev↔prod id mapping from
// natural-key matches). Idempotent and non-destructive, so it is safe to leave on.
const SHOW_INIT_MAPPING = true;

// ── Status styling ────────────────────────────────────────────────────────────

const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  dev_only:  { label: 'Dev Only',  color: 'warning' },
  different: { label: 'Different', color: 'error' },
  prod_only: { label: 'Prod Only', color: 'default' },
  identical: { label: 'Identical', color: 'success' },
};

const canSync = (status: string, direction: SyncDirection) =>
  direction === 'dev_to_prod'
    ? status === 'dev_only' || status === 'different'
    : status === 'prod_only' || status === 'different';

// The id to send to the sync endpoint depends on direction:
// dev_to_prod uses the dev record id, prod_to_dev uses the prod record id.
const syncIdFor = (record: RecordDiff, direction: SyncDirection): number | undefined =>
  direction === 'dev_to_prod' ? record.dev_record?.id : record.prod_record?.id;

// ── Summary view ──────────────────────────────────────────────────────────────

interface SummaryViewProps {
  summary: DiffSummaryResponse;
  onDrillIn: (table: ConfigTable) => void;
  onRefresh: () => void;
  loading: boolean;
  direction: SyncDirection;
  onDirectionChange: (d: SyncDirection) => void;
}

const SummaryView: React.FC<SummaryViewProps> = ({ summary, onDrillIn, onRefresh, loading, direction, onDirectionChange }) => (
  <Box>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
      <Box>
        <Typography variant="h5" fontWeight={600}>Config Sync</Typography>
        <Typography variant="body2" color="text.secondary">
          <code>{summary.dev_url}</code>
        </Typography>
      </Box>
      <Box display="flex" gap={2} alignItems="center">
        <ToggleButtonGroup
          value={direction}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) onDirectionChange(val); }}
        >
          <ToggleButton value="dev_to_prod">Dev → Prod</ToggleButton>
          <ToggleButton value="prod_to_dev">Prod → Dev</ToggleButton>
        </ToggleButtonGroup>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>
    </Box>

    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600 }}>Table</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'warning.main' }}>Dev Only</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'error.main' }}>Different</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>Prod Only</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'success.main' }}>Identical</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.tables.map((t) => {
            const hasChanges = t.dev_only > 0 || t.different > 0;
            const isError = t.identical === -1;
            return (
              <TableRow
                key={t.table}
                hover
                sx={{ backgroundColor: hasChanges ? 'rgba(255,152,0,0.04)' : undefined }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  {TABLE_LABELS[t.table as ConfigTable] ?? t.table}
                </TableCell>
                <TableCell align="center">
                  {isError ? '—' : t.dev_only > 0 ? (
                    <Chip label={t.dev_only} size="small" color="warning" />
                  ) : <Typography variant="body2" color="text.secondary">0</Typography>}
                </TableCell>
                <TableCell align="center">
                  {isError ? '—' : t.different > 0 ? (
                    <Chip label={t.different} size="small" color="error" />
                  ) : <Typography variant="body2" color="text.secondary">0</Typography>}
                </TableCell>
                <TableCell align="center">
                  {isError ? '—' : <Typography variant="body2" color="text.secondary">{t.prod_only}</Typography>}
                </TableCell>
                <TableCell align="center">
                  {isError ? (
                    <Tooltip title={t.error || 'Unknown error'} arrow>
                      <Chip label="Error" size="small" color="default" sx={{ cursor: 'help' }} />
                    </Tooltip>
                  ) : <Typography variant="body2" color="text.secondary">{t.identical}</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => onDrillIn(t.table as ConfigTable)}
                    disabled={isError}
                  >
                    View →
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  </Box>
);

// ── Inline word diff ──────────────────────────────────────────────────────────

interface TokenOp { type: 'same' | 'del' | 'ins'; text: string }

// HTML tags are treated as single tokens so attributes don't explode the count.
function tokenize(s: string): string[] {
  return s.match(/<[^>]+>|[^<\s]+|\s+/g) ?? [];
}

function computeOps(aStr: string, bStr: string): TokenOp[] {
  const A = tokenize(aStr);
  const B = tokenize(bStr);
  const m = A.length, n = B.length;

  if (m * n > 500_000) {
    return [{ type: 'del', text: aStr }, { type: 'ins', text: bStr }];
  }

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = A[i-1] === B[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);

  const ops: TokenOp[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && A[i-1] === B[j-1]) {
      ops.push({ type: 'same', text: A[i-1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      ops.push({ type: 'ins', text: B[j-1] }); j--;
    } else {
      ops.push({ type: 'del', text: A[i-1] }); i--;
    }
  }
  return ops.reverse();
}

const CONTEXT = 8;

const InlineDiff: React.FC<{ ops: TokenOp[]; side: 'a' | 'b'; highlightColor: string }> = ({ ops, side, highlightColor }) => {
  const sideOps = ops
    .filter(op => op.type === 'same' || (side === 'a' ? op.type === 'del' : op.type === 'ins'))
    .map(op => ({ text: op.text, changed: op.type !== 'same' }));

  const n = sideOps.length;
  const show = new Array(n).fill(false);
  for (let i = 0; i < n; i++) {
    if (sideOps[i].changed) {
      for (let j = Math.max(0, i - CONTEXT); j <= Math.min(n - 1, i + CONTEXT); j++) show[j] = true;
    }
  }

  if (!show.some(Boolean)) return null;

  const nodes: React.ReactNode[] = [];
  let k = 0;
  if (show.indexOf(true) > 0) nodes.push(<span key={k++} style={{ color: '#aaa' }}>…</span>);

  let i = 0;
  while (i < n) {
    if (show[i]) {
      const { text, changed } = sideOps[i];
      nodes.push(changed
        ? <mark key={k++} style={{ backgroundColor: highlightColor, borderRadius: 2, padding: '0 1px' }}>{text}</mark>
        : <span key={k++}>{text}</span>
      );
      i++;
    } else {
      while (i < n && !show[i]) i++;
      nodes.push(<span key={k++} style={{ color: '#aaa' }}> … </span>);
    }
  }

  if (show.lastIndexOf(true) < n - 1) nodes.push(<span key={k++} style={{ color: '#aaa' }}>…</span>);

  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>
      {nodes}
    </span>
  );
};

// ── Record diff expander ──────────────────────────────────────────────────────

const RecordExpander: React.FC<{ record: RecordDiff }> = ({ record }) => {
  const [open, setOpen] = useState(false);
  if (!record.diff_fields.length) return null;

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(!open)}>
        {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </IconButton>
      <Collapse in={open}>
        <Box
          sx={{
            mt: 1, p: 1.5, borderRadius: 1,
            backgroundColor: 'grey.50', border: '1px solid', borderColor: 'divider',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 0.5, color: 'primary.main' }}>Dev</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 0.5, color: 'text.secondary' }}>Prod</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.diff_fields.map((f) => {
                const devStr = JSON.stringify(record.dev_record?.[f] ?? null, null, 2);
                const prodStr = JSON.stringify(record.prod_record?.[f] ?? null, null, 2);
                const ops = computeOps(devStr, prodStr);
                return (
                  <TableRow key={f}>
                    <TableCell sx={{ py: 0.5, fontFamily: 'monospace', fontSize: 12 }}>{f}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <InlineDiff ops={ops} side="a" highlightColor="rgba(76,175,80,0.4)" />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <InlineDiff ops={ops} side="b" highlightColor="rgba(244,67,54,0.4)" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </>
  );
};

// ── Table detail view ─────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'dev_only' | 'different' | 'prod_only' | 'identical';

interface TableDetailViewProps {
  table: ConfigTable;
  detail: TableDiffResponse;
  onBack: () => void;
  onRefresh: () => void;
  loading: boolean;
  direction: SyncDirection;
  onDirectionChange: (d: SyncDirection) => void;
}

const TableDetailView: React.FC<TableDetailViewProps> = ({ table, detail, onBack, onRefresh, loading, direction, onDirectionChange }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<Record<string, SyncResultItem>>({});

  const filtered = detail.records.filter(
    (r) => statusFilter === 'all' || r.status === statusFilter
  );

  const syncable = filtered.filter((r) => canSync(r.status, direction));
  const allSyncableSelected = syncable.length > 0 && syncable.every((r) => selected.has(r.natural_key));

  const toggleAll = () => {
    if (allSyncableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(syncable.map((r) => r.natural_key)));
    }
  };

  const toggleOne = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const doSync = useCallback(async (records: RecordDiff[]) => {
    const ids = records
      .map((r) => syncIdFor(r, direction))
      .filter((id): id is number => id != null);
    if (ids.length === 0) {
      toast.error('No syncable record ids found');
      return;
    }
    setSyncing(true);
    try {
      const res = await configSyncService.syncRecords({ table, ids, direction });
      const ok = res.results.filter((r) => r.success).length;
      const fail = res.results.filter((r) => !r.success).length;
      if (fail === 0) toast.success(`Synced ${ok} record${ok !== 1 ? 's' : ''}`);
      else toast.error(`${ok} synced, ${fail} failed`);
      setSelected(new Set());
      setSyncResults({});
      onRefresh();
    } catch (err: any) {
      toast.error(getApiError(err, 'Sync failed'));
    } finally {
      setSyncing(false);
    }
  }, [table, direction, onRefresh]);

  const counts = {
    all: detail.records.length,
    dev_only: detail.records.filter((r) => r.status === 'dev_only').length,
    different: detail.records.filter((r) => r.status === 'different').length,
    prod_only: detail.records.filter((r) => r.status === 'prod_only').length,
    identical: detail.records.filter((r) => r.status === 'identical').length,
  };

  const filterOptions: { value: StatusFilter; label: string; color?: 'warning' | 'error' | 'default' | 'success' }[] = [
    { value: 'all', label: `All (${counts.all})` },
    { value: 'dev_only', label: `Dev Only (${counts.dev_only})`, color: 'warning' },
    { value: 'different', label: `Different (${counts.different})`, color: 'error' },
    { value: 'prod_only', label: `Prod Only (${counts.prod_only})`, color: 'default' },
    { value: 'identical', label: `Identical (${counts.identical})`, color: 'success' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack} size="small"><BackIcon /></IconButton>
          <Typography variant="body2" color="text.secondary">Config Sync /</Typography>
          <Typography variant="body2" fontWeight={600}>{TABLE_LABELS[table]}</Typography>
        </Box>
        <ToggleButtonGroup
          value={direction}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) onDirectionChange(val); }}
        >
          <ToggleButton value="dev_to_prod">Dev → Prod</ToggleButton>
          <ToggleButton value="prod_to_dev">Prod → Dev</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" gap={1} flexWrap="wrap">
          {filterOptions.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              color={statusFilter === opt.value ? (opt.color || 'primary') : 'default'}
              variant={statusFilter === opt.value ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(opt.value)}
              size="small"
              clickable
            />
          ))}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {selected.size > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              onClick={() => doSync(detail.records.filter((r) => selected.has(r.natural_key)))}
              disabled={syncing}
            >
              Sync Selected ({selected.size})
            </Button>
          )}
          <IconButton onClick={onRefresh} disabled={loading} size="small">
            {loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selected.size > 0 && !allSyncableSelected}
                  checked={allSyncableSelected}
                  onChange={toggleAll}
                  disabled={syncable.length === 0}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Changed Fields</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records
                </TableCell>
              </TableRow>
            )}
            {filtered.map((record) => {
              const syncResult = syncResults[record.natural_key];
              const isSyncable = canSync(record.status, direction);
              const displayKey = record.natural_key.length > 60
                ? record.natural_key.slice(0, 60) + '…'
                : record.natural_key;

              return (
                <React.Fragment key={record.natural_key}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: syncResult?.success
                        ? 'rgba(76,175,80,0.05)'
                        : syncResult && !syncResult.success
                        ? 'rgba(244,67,54,0.05)'
                        : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(record.natural_key)}
                        onChange={() => toggleOne(record.natural_key)}
                        disabled={!isSyncable}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={record.natural_key.length > 60 ? record.natural_key : ''}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {displayKey}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_CHIP[record.status]?.label}
                        color={STATUS_CHIP[record.status]?.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {record.diff_fields.length > 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                          {record.diff_fields.join(', ')}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {syncResult && (
                        <Chip
                          label={syncResult.success ? syncResult.action : 'failed'}
                          color={syncResult.success ? 'success' : 'error'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {isSyncable && (
                        <Button
                          size="small"
                          startIcon={syncing ? <CircularProgress size={12} /> : <SyncIcon />}
                          onClick={() => doSync([record])}
                          disabled={syncing}
                        >
                          Sync
                        </Button>
                      )}
                      {record.diff_fields.length > 0 && (
                        <RecordExpander record={record} />
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const ConfigSyncPage: React.FC = () => {
  const [summary, setSummary] = useState<DiffSummaryResponse | null>(null);
  const [selectedTable, setSelectedTable] = useState<ConfigTable | null>(null);
  const [detail, setDetail] = useState<TableDiffResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<SyncDirection>('dev_to_prod');
  const [initializingMapping, setInitializingMapping] = useState(false);
  const [mappingReport, setMappingReport] = useState<InitializeMappingResponse | null>(null);

  const handleInitializeMapping = useCallback(async () => {
    setInitializingMapping(true);
    try {
      const report = await configSyncService.initializeMapping();
      setMappingReport(report);
      toast.success('ID mapping initialized');
    } catch (err: any) {
      toast.error(getApiError(err, 'Failed to initialize ID mapping'));
    } finally {
      setInitializingMapping(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);
    try {
      const data = await configSyncService.getDiffSummary();
      setSummary(data);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load diff summary'));
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const loadDetail = useCallback(async (table: ConfigTable) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const data = await configSyncService.getTableDiff(table);
      setDetail(data);
    } catch (err: any) {
      setError(getApiError(err, 'An error occurred') || `Failed to load diff for ${table}`);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleDrillIn = (table: ConfigTable) => {
    setSelectedTable(table);
    setDetail(null);
    loadDetail(table);
  };

  const handleBack = () => {
    setSelectedTable(null);
    setDetail(null);
    setError(null);
    loadSummary();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {SHOW_INIT_MAPPING && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={initializingMapping ? <CircularProgress size={14} /> : <LinkIcon />}
            onClick={handleInitializeMapping}
            disabled={initializingMapping}
          >
            Initialize ID Mapping
          </Button>
        </Box>
      )}

      <Dialog open={!!mappingReport} onClose={() => setMappingReport(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ID Mapping Report</DialogTitle>
        <DialogContent dividers>
          {mappingReport && mappingReport.tables.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Table</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Mapped</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Dev Only</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Prod Only</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Ambiguous</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappingReport.tables.map((t) => (
                  <TableRow key={t.table}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {TABLE_LABELS[t.table as ConfigTable] ?? t.table}
                    </TableCell>
                    <TableCell align="center">{t.mapped}</TableCell>
                    <TableCell align="center">{t.dev_only}</TableCell>
                    <TableCell align="center">{t.prod_only}</TableCell>
                    <TableCell align="center">
                      {t.ambiguous > 0 ? (
                        <Chip label={t.ambiguous} size="small" color="warning" />
                      ) : 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No mapping data returned.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingReport(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {!selectedTable ? (
        loadingSummary && !summary ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : summary ? (
          <SummaryView
            summary={summary}
            onDrillIn={handleDrillIn}
            onRefresh={loadSummary}
            loading={loadingSummary}
            direction={direction}
            onDirectionChange={setDirection}
          />
        ) : null
      ) : (
        loadingDetail && !detail ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : detail ? (
          <TableDetailView
            table={selectedTable}
            detail={detail}
            onBack={handleBack}
            onRefresh={() => loadDetail(selectedTable)}
            loading={loadingDetail}
            direction={direction}
            onDirectionChange={setDirection}
          />
        ) : null
      )}
    </Box>
  );
};

export default ConfigSyncPage;
